import { Box, Tooltip, Typography } from "@mui/material";
import { Theme, alpha, useTheme } from "@mui/material/styles";
import {
  BaseEdge,
  Edge,
  EdgeProps,
  Handle,
  Node,
  NodeProps,
  Position,
} from "@xyflow/react";
import React, { useId } from "react";

import { monospaceStyle } from "../../../theme";
import { CapabilityMapNode } from "../../../types/analytics";
import { hatchOf } from "../Treemap/TreemapCanvas";
import { LABEL_WIDTH, pairPath } from "./capabilityMapLayout";

// How much of its area's colour fills a dot, and how much of it borders one
// under the floor
export const DOT_TINT = 0.3;
export const THIN_BORDER = 0.4;

// How a dot is drawn: stated as a need, under the floor, or plainly claimed
export type DotLook = "need" | "thin" | "claimed";

export const dotLookOf = (node: CapabilityMapNode): DotLook =>
  node.need_count > 0 ? "need" : node.thin ? "thin" : "claimed";

// The fill and the border of a dot in the colour of its effect area, shared
// by the canvas and the legend. The tint lies on the surface's own colour: a
// line ends at the centre of a dot and does not show through it.
export const dotSx = (theme: Theme, look: DotLook, thin: boolean, colour: string) =>
  ({
    borderRadius: "50%",
    boxSizing: "border-box",
    backgroundColor: "background.paper",
    backgroundImage: thin
      ? hatchOf(theme)
      : `linear-gradient(${alpha(colour, DOT_TINT)}, ${alpha(colour, DOT_TINT)})`,
    border:
      look === "need"
        ? `2px dashed ${theme.palette.chart.need}`
        : `2px solid ${thin ? alpha(colour, THIN_BORDER) : colour}`,
  }) as const;

// The opacity of a node or a line the selection does not keep lit
const DIMMED_OPACITY = 0.25;

// The name a node goes by: its capability, then its qualifier
export const nodeName = (node: CapabilityMapNode): string => {
  const name = node.capability.name ?? node.capability.id;
  return node.qualifier
    ? `${name} · ${node.qualifier.name ?? node.qualifier.id}`
    : name;
};

export const counted = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

export interface RegionData extends Record<string, unknown> {
  label: string;
  colour: string;
}

export type RegionFlowNode = Node<RegionData, "region">;

// How much of its colour a region is washed and outlined in
const REGION_WASH = 0.05;
const REGION_OUTLINE = 0.28;

// The backing of the text under a dot: the surface and the wash of the dot's
// region over it, so that a line that passes under the text does not strike
// it through and the backing does not show on the region
const backingSx = (theme: Theme, colour: string) =>
  ({
    px: "4px",
    borderRadius: "4px",
    backgroundColor: alpha(theme.palette.background.paper, 0.9),
    backgroundImage: `linear-gradient(${alpha(colour, REGION_WASH)}, ${alpha(colour, REGION_WASH)})`,
  }) as const;

// A labelled surface per effect area, in its colour, drawn beneath the dots
export const RegionNode: React.FC<NodeProps<RegionFlowNode>> = ({ data }) => (
  <Box
    role="group"
    aria-label={`Effect area ${data.label}`}
    data-colour={data.colour}
    sx={{
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
      border: `1px solid ${alpha(data.colour, REGION_OUTLINE)}`,
      borderRadius: "16px",
      backgroundColor: alpha(data.colour, REGION_WASH),
      p: "8px 12px",
      pointerEvents: "none",
    }}
  >
    <Typography
      component="h3"
      sx={{
        m: 0,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1.45,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: data.colour,
      }}
    >
      {data.label}
    </Typography>
  </Box>
);

export interface DotData extends Record<string, unknown> {
  node: CapabilityMapNode;
  radius: number;
  // The colour of the node's effect area
  colour: string;
  selected: boolean;
  dimmed: boolean;
  onSelect: (id: string) => void;
  // The pointer or the keyboard is on the node, or has left it (null)
  onPoint: (id: string | null) => void;
}

export type DotFlowNode = Node<DotData, "dot">;

const HANDLE_STYLE: React.CSSProperties = {
  left: "50%",
  top: "50%",
  opacity: 0,
  pointerEvents: "none",
};

// A capability: a dot in the colour of its effect area, sized by its
// products, with its label and its counts under it; dashed in amber when
// hosts state it as a need, hatched when thin
export const DotNode: React.FC<NodeProps<DotFlowNode>> = ({ data }) => {
  const { node, radius, colour, selected, dimmed, onSelect, onPoint } = data;
  const theme = useTheme();
  const needed = node.need_count > 0;
  const size = radius * 2;
  const look = dotLookOf(node);
  return (
    <Box
      role="button"
      tabIndex={0}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(node.id);
        }
      }}
      className="nodrag nopan"
      aria-label={nodeName(node)}
      aria-pressed={selected}
      data-thin={node.thin ? "true" : "false"}
      data-needed={needed ? "true" : "false"}
      data-dimmed={dimmed ? "true" : "false"}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(node.id);
      }}
      onMouseEnter={() => onPoint(node.id)}
      onMouseLeave={() => onPoint(null)}
      onFocus={() => onPoint(node.id)}
      onBlur={() => onPoint(null)}
      sx={{
        width: LABEL_WIDTH,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "3px",
        p: 0,
        border: 0,
        background: "none",
        color: "inherit",
        font: "inherit",
        textAlign: "center",
        cursor: "pointer",
        opacity: dimmed ? DIMMED_OPACITY : 1,
        transition: "opacity 120ms",
      }}
    >
      {/* The lines of the pairs end at the centre of the dot */}
      <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <Handle type="target" position={Position.Top} isConnectable={false} style={HANDLE_STYLE} />
        <Box
          data-testid="capability-dot"
          data-look={look}
          data-colour={colour}
          data-selected={selected ? "true" : "false"}
          sx={{
            ...dotSx(theme, look, node.thin, colour),
            width: "100%",
            height: "100%",
            // The selection is ringed in white, off the dot: every colour
            // of the map stands for an effect area
            boxShadow: (theme) =>
              selected
                ? `0 0 0 3px ${theme.palette.background.paper}, 0 0 0 5px ${theme.palette.text.primary}`
                : "none",
          }}
        />
        <Handle type="source" position={Position.Bottom} isConnectable={false} style={HANDLE_STYLE} />
      </Box>
      <Box
        component="span"
        sx={{
          ...backingSx(theme, colour),
          fontSize: "11.5px",
          lineHeight: 1.45,
          fontWeight: node.thin ? 400 : 500,
          color: node.thin ? "text.disabled" : "text.primary",
          overflowWrap: "anywhere",
        }}
      >
        {nodeName(node)}
      </Box>
      <Box
        component="span"
        sx={{
          ...monospaceStyle,
          // On the name's backing: a line that passes under the node does
          // not strike the count through
          ...backingSx(theme, colour),
          fontSize: "10.5px",
          lineHeight: 1.4,
          color: node.thin ? "text.disabled" : "text.secondary",
        }}
      >
        <span>{counted(node.product_count, "product")}</span>
        {needed && (
          <span
            aria-label={`Stated as a need by ${counted(node.need_count, "host")}`}
          >
            {` · need ${node.need_count}`}
          </span>
        )}
      </Box>
    </Box>
  );
};

export interface PairData extends Record<string, unknown> {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  a: string;
  b: string;
  jaccard: number;
  support: number;
  thin: boolean;
  // The colours of the effect areas of the two ends
  from: string;
  to: string;
  // What the line says, in a sentence
  reading: string;
  // Whether the node in focus is one of the pair
  active: boolean;
  dimmed: boolean;
}

export type PairFlowEdge = Edge<PairData, "pair">;

// Width (px) of the band along a line that answers the pointer
const HOVER_WIDTH = 12;

// How much of a line shows: at rest, lit by the focus, and out of it
export const LINE_OPACITY = { rest: 0.55, active: 1, dimmed: 0.07 } as const;

// The width (px) of a line: 1 to 6, by its Jaccard
export const lineWidthOf = (jaccard: number): number =>
  1 + 5 * Math.max(0, Math.min(1, jaccard));

// The share of a whole as a percentage, to the point
export const percent = (share: number): string =>
  `${Math.round(Math.max(0, Math.min(1, share)) * 100)}%`;

// What a pair says, in a sentence: how many products have both, and what
// share that is of the products that have either
export const pairReading = (
  a: CapabilityMapNode,
  b: CapabilityMapNode,
  support: number,
  jaccard: number,
): string => {
  const either = a.product_count + b.product_count - support;
  return (
    `${counted(support, "product")} ${support === 1 ? "is" : "are"} said to have both ` +
    `${nodeName(a)} and ${nodeName(b)}: ${percent(jaccard)} of the ${either} said to have either`
  );
};

// A pair of capabilities claimed by the same products: a line between the
// dots, heavier the nearer Jaccard is to 1, dashed when thin. It runs from
// the colour of one end's effect area to the colour of the other's.
export const PairEdge: React.FC<EdgeProps<PairFlowEdge>> = ({ id, data }) => {
  const gradientId = `pair-colours-${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  if (!data) {
    return null;
  }
  const path = pairPath(data.x1, data.y1, data.x2, data.y2);
  const mixed = data.from !== data.to;
  return (
    <>
      {mixed && (
        <defs>
          <linearGradient
            id={gradientId}
            gradientUnits="userSpaceOnUse"
            x1={data.x1}
            y1={data.y1}
            x2={data.x2}
            y2={data.y2}
          >
            <stop offset="0%" stopColor={data.from} />
            <stop offset="100%" stopColor={data.to} />
          </linearGradient>
        </defs>
      )}
      <BaseEdge
        id={id}
        path={path}
        interactionWidth={0}
        style={{
          stroke: mixed ? `url(#${gradientId})` : data.from,
          strokeWidth: lineWidthOf(data.jaccard),
          strokeLinecap: "round",
          strokeDasharray: data.thin ? "5 5" : undefined,
          opacity: data.dimmed
            ? LINE_OPACITY.dimmed
            : data.active
              ? LINE_OPACITY.active
              : LINE_OPACITY.rest,
          transition: "opacity 120ms",
        }}
      />
      <Tooltip title={data.reading} followCursor enterDelay={150}>
        <path
          d={path}
          fill="none"
          stroke="transparent"
          strokeWidth={HOVER_WIDTH}
          data-testid="pair-line"
          data-a={data.a}
          data-b={data.b}
          data-jaccard={data.jaccard}
          data-thin={data.thin ? "true" : "false"}
          data-active={data.active ? "true" : "false"}
          data-from={data.from}
          data-to={data.to}
        />
      </Tooltip>
    </>
  );
};
